import React, { useState, useMemo, useEffect } from "react";
import { Modal, Form, Input, ConfigProvider, Select, Card } from "antd";

const { Option } = Select;
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { hasRequiredScopes } from "../../utils/roleUtils";
import { isDashmoteStaffEmail } from "../../utils/isDashmoteStaffEmail";
import ScopePicker from "@web/components/InternalConfigurationTable/inviteUsers/ScopePicker";
import AccountTypeSelect from "../AccountTypeSelect";
import DefaultWorkspaceSelect from "../DefaultWorkspaceSelect";
import { emptyProductScopes } from "../productScopes";
import { useDefaultWorkspace } from "../../hooks/useDefaultWorkspace";
import "./InviteUsersModal.css";

const InviteUsersModal = ({
  open,
  onOk,
  onCancel,
  form,
  className = "",
  width = 720,
}) => {
  // Get workspace data from Redux. allWorkspaces (flat) drives the workspace
  // dropdown; hierarchicalWorkspaces is the page-level market scope used for the
  // per-product market options (matches User Scope Management).
  const { allWorkspaces, hierarchicalWorkspaces } = useSelector(
    (state) => state.workspace
  );

  // Local state for modal
  const [emailSearchValue, setEmailSearchValue] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  // Staff-only account classification: null (regular) | "demo" | "test".
  // Applies to every invited email. Dashmote-admin only; the backend re-gates
  // on the caller and enforces demo⇒@dashmote.com.
  const [accountType, setAccountType] = useState(null);
  const [workspaceAssignments, setWorkspaceAssignments] = useState([
    {
      id: 1,
      workspaceId: null,
      role: "normal_user",
      product_scopes: emptyProductScopes(),
    },
  ]);

  const {
    assignedWorkspaces,
    effectiveDefault,
    pick: pickDefaultWorkspace,
    reset: resetDefaultWorkspace,
    discardIfUnassigned: discardDefaultIfUnassigned,
  } = useDefaultWorkspace(workspaceAssignments);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      // Reset to initial state when modal opens
      setEmailSearchValue("");
      setSelectedEmails([]);
      setAccountType(null);
      resetDefaultWorkspace();
      setWorkspaceAssignments([
        {
          id: 1,
          workspaceId: null,
          role: "normal_user",
          product_scopes: emptyProductScopes(),
        },
      ]);
    }
  }, [open, resetDefaultWorkspace]);

  // Demo applies to every invited email, so it's only valid when ALL selected
  // emails are @dashmote.com. If the selection changes to include a non-staff
  // email while Demo is selected, revert to Regular (the backend would reject
  // demo on a non-@dashmote.com address).
  const allEmailsDashmote =
    selectedEmails.length > 0 &&
    selectedEmails.every((e) => isDashmoteStaffEmail(e.email));
  useEffect(() => {
    if (accountType === "demo" && !allEmailsDashmote) {
      setAccountType(null);
    }
  }, [allEmailsDashmote, accountType]);

  // Markets for a workspace come from its page-level scope (hierarchicalWorkspaces).
  const getMarketsForWorkspace = (workspaceId) => {
    const client = hierarchicalWorkspaces?.[workspaceId];
    if (!client) return [];
    const markets = [];
    Object.values(client.regions || {}).forEach((region) => {
      region.markets.forEach((m) => {
        if (!markets.includes(m)) markets.push(m);
      });
    });
    return markets;
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Helper functions
  const handleEmailInputChange = (e) => {
    setEmailSearchValue(e.target.value);
  };

  const processEmailInput = (inputValue) => {
    // Split by various delimiters: comma, semicolon, space, tab, newline
    const emailParts = inputValue
      .split(/[,;\s\n\t]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const validEmails = [];
    const invalidEmails = [];

    emailParts.forEach((email) => {
      if (isValidEmail(email)) {
        // Check if email is not already selected
        if (
          !selectedEmails.find((selectedEmail) => selectedEmail.email === email)
        ) {
          validEmails.push({
            name: "", // Empty name by default, user needs to fill it
            email: email,
          });
        }
      } else if (email.length > 0) {
        invalidEmails.push(email);
      }
    });

    // Add valid emails to selection
    if (validEmails.length > 0) {
      setSelectedEmails([...selectedEmails, ...validEmails]);
    }

    // Clear input if all emails were processed
    const remainingInput = invalidEmails.join(", ");
    setEmailSearchValue(remainingInput);

    return { validEmails, invalidEmails };
  };

  // Update user name in the selected emails list
  const handleNameChange = (email, newName) => {
    setSelectedEmails(
      selectedEmails.map((user) =>
        user.email === email ? { ...user, name: newName } : user
      )
    );
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      processEmailInput(emailSearchValue);
    }
  };

  const handleEmailBlur = () => {
    if (emailSearchValue.trim()) {
      processEmailInput(emailSearchValue);
    }
  };

  const handleEmailRemove = (emailToRemove) => {
    Modal.confirm({
      title: "Remove User?",
      content: `Are you sure you want to remove ${emailToRemove} from the invite list?`,
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: () => {
        setSelectedEmails(
          selectedEmails.filter((email) => email.email !== emailToRemove)
        );
      },
    });
  };

  // Workspace assignment handlers
  const addWorkspaceAssignment = () => {
    const newId = Math.max(...workspaceAssignments.map((w) => w.id)) + 1;
    setWorkspaceAssignments([
      ...workspaceAssignments,
      {
        id: newId,
        workspaceId: null,
        role: "normal_user",
        product_scopes: emptyProductScopes(),
      },
    ]);
  };

  const removeWorkspaceAssignment = (id) => {
    if (workspaceAssignments.length > 1) {
      const remaining = workspaceAssignments.filter((w) => w.id !== id);
      setWorkspaceAssignments(remaining);
      discardDefaultIfUnassigned(remaining);
    }
  };

  const updateWorkspaceAssignment = (id, field, value) => {
    const next = workspaceAssignments.map((w) => {
      if (w.id === id) {
        const updatedAssignment = { ...w, [field]: value };

        // Switching to Super User (full access) or to a different workspace
        // (markets differ) resets the per-product market scope.
        if (
          (field === "role" && value === "super_admin") ||
          field === "workspaceId"
        ) {
          updatedAssignment.product_scopes = emptyProductScopes();
        }

        return updatedAssignment;
      }
    return w;
    });
    setWorkspaceAssignments(next);
    // Re-pointing a row away from the picked workspace invalidates the pick the
    // same way deleting the row does, so discard it here too. Without this the
    // pick resurfaces as the default when the row is pointed back.
    if (field === "workspaceId") {
      discardDefaultIfUnassigned(next);
    }
  };

  const handleOk = () => {
    // Super User maps to super_admin server-side (no product_scopes); every other
    // role carries its per-product market scope as-is.
    const processedAssignments = workspaceAssignments.filter((w) => w.workspaceId);

    const formData = {
      emails: selectedEmails,
      workspaceAssignments: processedAssignments,
      accountType,
      defaultWorkspaceId: effectiveDefault,
    };

    onOk(formData);
  };

  const handleCancel = () => {
    // Reset form and state
    setEmailSearchValue("");
    setSelectedEmails([]);
    setAccountType(null);
    resetDefaultWorkspace();
    setWorkspaceAssignments([
      {
        id: 1,
        workspaceId: null,
        role: "normal_user",
        product_scopes: emptyProductScopes(),
      },
    ]);
    onCancel();
  };

  // Convert workspace data to select options - aggregate markets for each unique id_client
  // First create a deep copy of allWorkspaces to avoid mutating Redux state
  const workspacesCopy = useMemo(() => {
    return JSON.parse(JSON.stringify(allWorkspaces));
  }, [allWorkspaces]);

  const workspaceMap = workspacesCopy.reduce((acc, workspace) => {
    const clientId = workspace.id_client;

    if (!acc[clientId]) {
      // First time seeing this client, create the entry
      acc[clientId] = {
        id_client: clientId,
        region: workspace.region,
        mapped_id_client: workspace.mapped_id_client,
        markets: workspace.markets ? [...workspace.markets] : [], // Create new array
        core_brands: workspace.core_brands ? [...workspace.core_brands] : [], // Create new array
        // Keep other properties from the first occurrence
        market_penetration: workspace.market_penetration,
        market_penetration_branded: workspace.market_penetration_branded,
        core_availability: workspace.core_availability,
        combo_presence: workspace.combo_presence,
        fixed_combo_presence: workspace.fixed_combo_presence,
        option_combo_presence: workspace.option_combo_presence,
        name_accuracy: workspace.name_accuracy,
        image_presence: workspace.image_presence,
      };
    } else {
      // Client already exists, merge the markets
      if (
        workspace.markets &&
        workspace.markets[0] &&
        !acc[clientId].markets.includes(workspace.markets[0])
      ) {
        acc[clientId].markets.push(workspace.markets[0]);
      }

      // Optionally merge core_brands if they're different
      if (workspace.core_brands && workspace.core_brands.length > 0) {
        const existingBrands = acc[clientId].core_brands.map((b) => b.name);
        workspace.core_brands.forEach((brand) => {
          if (!existingBrands.includes(brand.name)) {
            acc[clientId].core_brands.push(brand);
          }
        });
      }
    }

    return acc;
  }, {});

  // Convert to array and create options
  const uniqueWorkspaces = Object.values(workspaceMap);

  const workspaceOptions = uniqueWorkspaces.map((workspace) => ({
    value: workspace.id_client, // Use id_client as the value directly
    label: `${workspace.id_client} (${workspace.markets.length} markets)`, // Show market count for debugging
    workspace: workspace,
  }));

  return (
    <ConfigProvider
      theme={{
        components: {
          TreeSelect: {
            borderRadius: 16,
          },
          Modal: {
            borderRadiusLG: 16,
          },
        },
      }}
    >
      <Modal
        title={
          <div className="flex justify-between items-center pb-4 border-b border-gray-100">
            <h2 className="text-gray-900 text-xl font-semibold font-['Inter'] leading-tight">
              Invite Users to Workspaces
            </h2>
          </div>
        }
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        width={width}
        centered
        className={className}
        maskClosable={false}
        okText="Send Invitations"
        cancelText="Cancel"
        okButtonProps={{
          // Admin / User (non-Super) assignments must include at least one
          // market in either Market Lens or Execution Lens. Without this
          // guard, the backend silently writes product_scopes: null and
          // every scoped endpoint (e.g. /metrics/v2) fails on dashboard
          // load for the invited user.
          disabled:
            selectedEmails.length === 0 ||
            workspaceAssignments.filter((w) => w.workspaceId).length === 0 ||
            workspaceAssignments.some(
              (w) => w.workspaceId && !hasRequiredScopes(w)
            ),
          className:
            "bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 rounded-lg h-10 px-6 font-medium",
        }}
        cancelButtonProps={{
          className:
            "border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 rounded-lg h-10 px-6",
        }}
      >
        <div className="py-6 flex flex-col gap-6">
          <Form
            form={form}
            layout="vertical"
            className="w-full flex flex-col gap-6"
          >
            {/* Email Field */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
                  Email Addresses
                </label>
                <span className="text-xs text-gray-500 font-['Inter']">
                  Separate multiple emails with comma, space, or Enter
                </span>
              </div>
              <div className="px-3 py-2 rounded-lg border border-gray-300 hover:border-blue-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/10 transition-all duration-200 flex items-center gap-2 min-h-[40px] flex-wrap bg-white">
                {/* Selected Email Tags */}
                {selectedEmails.map((email, index) => (
                  <div
                    key={index}
                    className="px-2 py-1 bg-blue-50 rounded-md border border-blue-200 flex items-center gap-1.5 animate-fadeIn"
                  >
                    <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="text-blue-600"
                      >
                        <path
                          d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                        <polyline
                          points="22,6 12,13 2,6"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                    </div>
                    <span className="text-blue-700 text-xs font-medium font-['Inter']">
                      {email.email}
                    </span>
                    <button
                      className="w-3.5 h-3.5 flex items-center justify-center hover:bg-blue-200 rounded-full transition-colors duration-150"
                      onClick={() => handleEmailRemove(email.email)}
                    >
                      <svg
                        width="7"
                        height="7"
                        viewBox="0 0 8 8"
                        fill="none"
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <path
                          d="M6 2L2 6M2 2L6 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}

                {/* Email Input */}
                <div className="flex-1 min-w-[200px] flex items-center">
                  <Input
                    value={emailSearchValue}
                    onChange={handleEmailInputChange}
                    onKeyDown={handleEmailKeyDown}
                    onBlur={handleEmailBlur}
                    placeholder={
                      selectedEmails.length === 0
                        ? "Enter email addresses (separate with comma, space, or Enter)..."
                        : "Add more email addresses..."
                    }
                    className="w-full email-input"
                    style={{
                      border: "none",
                      boxShadow: "none",
                      height: "32px",
                    }}
                    size="middle"
                  />
                </div>
              </div>
            </div>

            {/* User Details - Show when there are selected emails */}
            {selectedEmails.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="flex justify-between items-center">
                  <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
                    User Details
                  </label>
                  <span className="text-xs text-gray-500 font-['Inter']">
                    Name is optional
                  </span>
                </div>
                <div className="flex flex-col gap-2 max-h-[240px] overflow-y-auto pr-2">
                  {selectedEmails.map((user, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-blue-600"
                        >
                          <path
                            d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle
                            cx="12"
                            cy="7"
                            r="4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-['Inter'] min-w-[60px]">
                            Name:
                          </span>
                          <Input
                            value={user.name}
                            onChange={(e) =>
                              handleNameChange(user.email, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            placeholder="Enter user name (optional)"
                            className="flex-1"
                            style={{
                              borderRadius: "6px",
                              fontSize: "13px",
                            }}
                            size="small"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-['Inter'] min-w-[60px]">
                            Email:
                          </span>
                          <span className="text-sm text-gray-700 font-['Inter']">
                            {user.email}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEmailRemove(user.email)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-all duration-200 flex-shrink-0"
                        title="Remove user"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Account type — staff-only classification, applied to
                all invited emails. Regular = normal user; Test = fixture hidden
                from client user lists; Demo scopes @dashmote.com staff (only
                selectable when every invited email is @dashmote.com). Backend
                gates to Dashmote admins. */}
            <Card
              size="small"
              style={{
                marginBottom: "1rem",
                borderRadius: "12px",
                border: "2px solid #722ed1",
                background: "linear-gradient(to bottom, #f9f0ff, #ffffff)",
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <span className="text-[#262626] text-[15px] font-semibold font-['Inter']">
                    Account type
                  </span>
                  <div className="text-xs text-gray-500 font-['Inter'] mt-1 flex flex-col gap-0.5 max-w-md">
                    <span>
                      <span className="font-semibold">Regular</span> — real
                      users; regular Dashmote accounts see every workspace,
                      others only their assigned ones.
                    </span>
                    <span>
                      <span className="font-semibold">Demo</span> — Dashmote
                      emails only, limited to their assigned workspaces.
                    </span>
                    <span>
                      <span className="font-semibold">Test</span> — internal
                      accounts, hidden from client-facing user lists.
                    </span>
                  </div>
                </div>
                <AccountTypeSelect
                  value={accountType}
                  onChange={setAccountType}
                  demoAllowed={allEmailsDashmote}
                />
              </div>
            </Card>

            {/* Workspace Assignments */}
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
                  Workspace Assignments
                </label>
                <button
                  type="button"
                  onClick={addWorkspaceAssignment}
                  className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-sm font-medium rounded-lg transition-all duration-200 border border-transparent hover:border-blue-200"
                >
                  <PlusOutlined className="text-xs" />
                  <span>Add Workspace</span>
                </button>
              </div>

              {/* Workspace Assignment Cards */}
              <div className="flex flex-col gap-4">
                {workspaceAssignments.map((assignment) => (
                  <Card
                    key={assignment.id}
                    className="border border-gray-200 hover:border-gray-300 transition-colors duration-200 rounded-lg shadow-sm hover:shadow-md"
                    size="small"
                  >
                    <div className="flex flex-col gap-4">
                      {/* Workspace Selection and Delete Button */}
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <label className="text-gray-700 text-xs font-medium font-['Inter'] mb-2 block">
                            Workspace
                          </label>
                          <Select
                            placeholder="Select a workspace"
                            value={assignment.workspaceId}
                            onChange={(value) =>
                              updateWorkspaceAssignment(
                                assignment.id,
                                "workspaceId",
                                value
                              )
                            }
                            className="w-full"
                            style={{ borderRadius: "8px" }}
                          >
                            {workspaceOptions.map((option) => (
                              <Option
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        {workspaceAssignments.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              removeWorkspaceAssignment(assignment.id)
                            }
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 mt-6 rounded-lg transition-all duration-200"
                            title="Remove workspace assignment"
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>

                      {/* Role Selection */}
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-700 text-xs font-medium font-['Inter']">
                          Role for this workspace
                        </label>
                        <div className="flex flex-col gap-1">
                          {[
                            {
                              value: "super_admin",
                              label: "Super Admin",
                              description:
                                "Full workspace access and user management",
                            },
                            {
                              value: "admin",
                              label: "Admin",
                              description:
                                "Manage workspace content and settings",
                            },
                            {
                              value: "normal_user",
                              label: "User",
                              description:
                                "View and interact with workspace content",
                            },
                          ].map((role) => (
                            <label
                              key={role.value}
                              className={`flex items-center py-3 px-3 cursor-pointer rounded-lg transition-all duration-200 ease-in-out group border-2 ${
                                assignment.role === role.value
                                  ? "border-blue-200 bg-blue-50 hover:bg-blue-100"
                                  : "border-transparent hover:bg-gray-50 hover:border-gray-200"
                              }`}
                              onClick={() =>
                                updateWorkspaceAssignment(
                                  assignment.id,
                                  "role",
                                  role.value
                                )
                              }
                            >
                              <input
                                type="radio"
                                name={`role-${assignment.id}`}
                                value={role.value}
                                checked={assignment.role === role.value}
                                onChange={() =>
                                  updateWorkspaceAssignment(
                                    assignment.id,
                                    "role",
                                    role.value
                                  )
                                }
                                className="w-4 h-4 text-blue-600 transition-colors duration-200 focus:ring-blue-500 focus:ring-2"
                              />
                              <div className="ml-3 flex flex-col">
                                <span
                                  className={`text-sm font-medium font-['Inter'] transition-colors duration-200 ${
                                    assignment.role === role.value
                                      ? "text-blue-900"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {role.label}
                                </span>
                                <span className="text-xs font-normal font-['Inter'] transition-colors duration-200 text-gray-500 mt-0.5">
                                  {role.description}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Scope Selection for this workspace - Only show if not Super Admin */}
                      {assignment.role !== "super_admin" && (
                        <div className="flex flex-col gap-3">
                          <label className="text-gray-700 text-xs font-medium font-['Inter']">
                            Scope for this workspace
                          </label>
                          {assignment.workspaceId ? (
                            <>
                              <ScopePicker
                                role={assignment.role}
                                markets={getMarketsForWorkspace(assignment.workspaceId)}
                                productScopes={assignment.product_scopes}
                                onChange={(next) =>
                                  updateWorkspaceAssignment(
                                    assignment.id,
                                    "product_scopes",
                                    next
                                  )
                                }
                                showLabel={false}
                              />
                              {!hasRequiredScopes(assignment) && (
                                <span className="text-xs text-red-500 font-['Inter']">
                                  Select at least one market on a product to grant
                                  access for this workspace.
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400 font-['Inter']">
                              Select a workspace first
                            </span>
                          )}
                        </div>
                      )}

                      {/* Super Admin Notice */}
                      {assignment.role === "super_admin" && (
                        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              className="text-blue-600"
                            >
                              <path
                                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-blue-800 text-sm font-medium font-['Inter']">
                              Super Admin Access
                            </span>
                            <span className="text-blue-600 text-xs font-normal font-['Inter']">
                              This role has access to all scopes and features in
                              the workspace
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <DefaultWorkspaceSelect
              workspaces={assignedWorkspaces}
              value={effectiveDefault}
              onChange={pickDefaultWorkspace}
              hint="The workspace these users see first when they sign in."
            />
          </Form>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default InviteUsersModal;
